<script setup lang="ts">
import ModifiersPanel from "@innoclouds/ui/templates/productCard/modifiersPanel/ModifiersPanel.vue";
import type { ModifiersPanelRule, ModifiersPanelTab, ModifiersPanelTile } from "@innoclouds/ui/templates/productCard/modifiersPanel/modifiersPanelTypes.ts";
import { useTranslation } from "i18next-vue";
import { computed, inject, ref, watch } from "vue";
import { usePdf } from "#/composables/usePdfMode";
import { SINGLE_MODIFIERS_GROUP_ID } from "#/constants/modifiers";
import { useDecorationStore } from "#/store/decoration";
import { useMainStore } from "#/store/main";
import { useThemeStore } from "#/store/theme";
import type { IGroupModifiers, ISelectModifiers } from "#/types/iCardProduct";
import { formatPrice, getLocaleName } from "#/utils/i18next";
import { liquidModifiersKey } from "./liquidModifiersContext";

const props = defineProps<{ groups: IGroupModifiers[]; open: boolean; price: string; decoration?: string }>();

const emit = defineEmits<{ close: []; apply: [] }>();

const { t, i18next } = useTranslation();

const mainStore = useMainStore();
const themeStore = useThemeStore();
const { isDesktop } = usePdf();
const decor = useDecorationStore().screen(props.decoration);

const ctx = inject(liquidModifiersKey);
if (!ctx) throw new Error("LiquidModifiersPanel: missing liquidModifiersKey provider");

const activeGroupId = ref<string | null>(null);

const isRequiredGroup = (group: IGroupModifiers) => group.required || group.min_amount > 0;
const visibleModifiers = (group: IGroupModifiers) => group.group_modifiers.filter((modifier) => !ctx.isHiddenModifier(modifier, group));
const countInGroup = (group: IGroupModifiers) => visibleModifiers(group).reduce((sum, modifier) => sum + ctx.getAmount(modifier, group), 0);
const groupLimit = (group: IGroupModifiers) => {
	if (group.id !== SINGLE_MODIFIERS_GROUP_ID) return group.max_amount;
	const total = group.group_modifiers.reduce((sum, modifier) => sum + (modifier.max_amount ?? 0), 0);
	return total > 0 ? total : group.max_amount;
};

const activeGroup = computed(() => props.groups.find((group) => group.id === activeGroupId.value) ?? props.groups[0] ?? null);
const isReady = computed(() => props.groups.every((group) => ctx.isGroupSatisfied(group)));

const activeTabId = computed({
	get: () => activeGroup.value?.id ?? null,
	set: (id: string | null) => {
		activeGroupId.value = id;
	},
});

const tabs = computed<ModifiersPanelTab[]>(() =>
	props.groups.map((group) => ({
		id: group.id,
		name: getLocaleName(group, i18next.language),
		isRequired: isRequiredGroup(group),
		isDone: ctx.isGroupSatisfied(group),
		count: `${countInGroup(group)}/${groupLimit(group)}`,
	})),
);

const rule = computed<ModifiersPanelRule | null>(() => {
	const group = activeGroup.value;
	if (!group) return null;
	return {
		isRequired: isRequiredGroup(group),
		badge: isRequiredGroup(group) ? t(`card.modifiers.required`) : t(`card.modifiers.optional`),
		text: groupLimit(group) === 1 ? t(`card.modifiers.choose-one`) : t(`card.modifiers.up-to`, { count: groupLimit(group) }),
	};
});

const tiles = computed<ModifiersPanelTile[]>(() => {
	const group = activeGroup.value;
	if (!group) return [];
	return visibleModifiers(group).map((modifier) => {
		const amount = ctx.getAmount(modifier, group);
		const price = ctx.getModifierPrice(modifier);
		return {
			id: modifier.id,
			name: getLocaleName(modifier, i18next.language),
			price: `+${formatPrice(Math.max(price, 0), mainStore.config_lib?.currency_symbol)}`,
			image: modifier.image_link || themeStore.data?.fallback_image || "",
			amount,
			isCounter: ctx.isSupportsModifierQuantity(modifier, group),
			canIncrement: ctx.canIncrementModifier(modifier, group),
			isDimmed: amount === 0 && !ctx.canIncrementModifier(modifier, group),
		};
	});
});

const findModifier = (id: string) => {
	const group = activeGroup.value;
	return group ? visibleModifiers(group).find((modifier) => modifier.id === id) : undefined;
};

const increment = (modifier: ISelectModifiers) => {
	const group = activeGroup.value;
	if (!group) return;
	if (ctx.getAmount(modifier, group) === 0) ctx.selectModifier(modifier, group);
	else ctx.incrementModifier(modifier, group);
};

const decrement = (modifier: ISelectModifiers) => {
	const group = activeGroup.value;
	if (group) ctx.decrementModifier(modifier, group);
};

const selectTile = (modifier: ISelectModifiers, isCounter: boolean) => {
	const group = activeGroup.value;
	if (!group) return;
	if (isCounter) increment(modifier);
	else ctx.selectModifier(modifier, group);
};

const onSelect = (id: string) => {
	const group = activeGroup.value;
	const modifier = findModifier(id);
	if (group && modifier) selectTile(modifier, ctx.isSupportsModifierQuantity(modifier, group));
};

const onIncrement = (id: string) => {
	const modifier = findModifier(id);
	if (modifier) increment(modifier);
};

const onDecrement = (id: string) => {
	const modifier = findModifier(id);
	if (modifier) decrement(modifier);
};

const apply = () => {
	const missing = props.groups.find((group) => !ctx.isGroupSatisfied(group));
	if (missing) activeGroupId.value = missing.id;
	emit("apply");
};

watch(
	() => props.open,
	(open) => {
		if (open) activeGroupId.value = props.groups[0]?.id ?? null;
	},
	{ immediate: true },
);
</script>

<template>
	<ModifiersPanel
		v-model:activeGroupId="activeTabId"
		:open="open"
		:tabs="tabs"
		:rule="rule"
		:tiles="tiles"
		:price="price"
		:ready="isReady"
		:applyLabel="t(`card.button.apply`)"
		:pdfMode="isDesktop"
		:decor="decor"
		@close="emit('close')"
		@apply="apply"
		@select="onSelect"
		@increment="onIncrement"
		@decrement="onDecrement"
	/>
</template>
